/**
 * Layer Panel
 * Displays layer list with drag-drop reordering, visibility, lock toggles, and add layer menu
 */

import React, { useCallback, useState, useRef, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd';
import { nanoid } from 'nanoid';
import { useTemplateStore } from '@/stores/designStore';
import type { Layer, TextLayer, ShapeLayer, PrimitiveLayer } from '@/types/layers';
import { createAlignmentPosition } from '@/types/position';
import { solid } from '@/types/brush';

// Layer type icons
const LAYER_ICONS: Record<string, string> = {
  text: 'T',
  image: '🖼',
  video: '🎬',
  audio: '🔊',
  shape: '◼',
  primitive: '✦',
  group: '📁',
};

// Primitive type display names
const PRIMITIVE_NAMES: Record<string, string> = {
  'eclipse-moon': 'Eclipse Moon',
  'star-field': 'Star Field',
  'wordmark': 'Wordmark',
  'app-badges': 'App Badges',
  'data-leak': 'Data Leak',
  'cta-button': 'CTA Button',
};

// Layer type options for add menu
const LAYER_TYPE_OPTIONS = [
  { type: 'text', label: 'Text', icon: 'T' },
  { type: 'shape-rect', label: 'Rectangle', icon: '◻' },
  { type: 'shape-circle', label: 'Circle', icon: '○' },
  { type: 'primitive-moon', label: 'Eclipse Moon', icon: '🌙' },
  { type: 'primitive-stars', label: 'Star Field', icon: '✦' },
  { type: 'primitive-wordmark', label: 'Wordmark', icon: '🏷' },
  { type: 'primitive-cta', label: 'CTA Button', icon: '▶' },
  { type: 'primitive-badges', label: 'App Badges', icon: '📱' },
] as const;

function createNewLayer(typeOption: string): Layer {
  const id = nanoid();
  const position = createAlignmentPosition('Center');

  switch (typeOption) {
    case 'text':
      return {
        id,
        type: 'text',
        name: 'New Text',
        position,
        visible: true,
        locked: false,
        opacity: 1,
        content: {
          text: 'New Text',
          fontSize: 24,
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          color: solid('#FFFFFF'),
          textAlign: 'center',
        },
      } as TextLayer;

    case 'shape-rect':
      return {
        id,
        type: 'shape',
        name: 'Rectangle',
        position,
        width: 100,
        height: 100,
        visible: true,
        locked: false,
        opacity: 1,
        content: {
          shapeType: 'rectangle',
          fill: solid('#FFFFFF'),
          borderRadius: 8,
        },
      } as ShapeLayer;

    case 'shape-circle':
      return {
        id,
        type: 'shape',
        name: 'Circle',
        position,
        width: 100,
        height: 100,
        visible: true,
        locked: false,
        opacity: 1,
        content: {
          shapeType: 'circle',
          fill: solid('#FFFFFF'),
        },
      } as ShapeLayer;

    case 'primitive-moon':
      return {
        id,
        type: 'primitive',
        name: 'Eclipse Moon',
        position,
        visible: true,
        locked: false,
        opacity: 1,
        content: {
          primitiveType: 'eclipse-moon',
          size: 200,
        },
      } as PrimitiveLayer;

    case 'primitive-stars':
      return {
        id,
        type: 'primitive',
        name: 'Star Field',
        position: createAlignmentPosition('TopLeft'),
        visible: true,
        locked: false,
        opacity: 1,
        content: {
          primitiveType: 'star-field',
          starCount: 100,
        },
      } as PrimitiveLayer;

    case 'primitive-wordmark':
      return {
        id,
        type: 'primitive',
        name: 'Wordmark',
        position,
        visible: true,
        locked: false,
        opacity: 1,
        content: {
          primitiveType: 'wordmark',
          size: 24,
        },
      } as PrimitiveLayer;

    case 'primitive-cta':
      return {
        id,
        type: 'primitive',
        name: 'CTA Button',
        position,
        visible: true,
        locked: false,
        opacity: 1,
        content: {
          primitiveType: 'cta-button',
          text: 'Get Started',
          props: { variant: 'glow', size: 'md' },
        },
      } as PrimitiveLayer;

    case 'primitive-badges':
      return {
        id,
        type: 'primitive',
        name: 'App Badges',
        position,
        visible: true,
        locked: false,
        opacity: 1,
        content: {
          primitiveType: 'app-badges',
          scale: 1,
        },
      } as PrimitiveLayer;

    default:
      return {
        id,
        type: 'text',
        name: 'New Layer',
        position,
        visible: true,
        locked: false,
        opacity: 1,
        content: {
          text: 'New Layer',
          fontSize: 24,
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          color: solid('#FFFFFF'),
          textAlign: 'center',
        },
      } as TextLayer;
  }
}

interface AddLayerMenuProps {
  onAdd: (layer: Layer) => void;
  onClose: () => void;
}

function AddLayerMenu({ onAdd, onClose }: AddLayerMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      className="absolute top-full right-0 mt-1 w-48 bg-navy-light border border-white/10 rounded-lg shadow-xl z-50 py-1"
    >
      {LAYER_TYPE_OPTIONS.map((option) => (
        <button
          key={option.type}
          onClick={() => {
            onAdd(createNewLayer(option.type));
            onClose();
          }}
          className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-white/10 hover:text-white flex items-center gap-2"
        >
          <span className="w-5 text-center">{option.icon}</span>
          {option.label}
        </button>
      ))}
    </div>
  );
}

interface LayerItemProps {
  layer: Layer;
  index: number;
  depth?: number;
  isSelected: boolean;
  isHovered: boolean;
  onSelect: (layerId: string, event: React.MouseEvent) => void;
  onToggleVisibility: (layerId: string) => void;
  onToggleLock: (layerId: string) => void;
  onHover: (layerId: string | null) => void;
  onDelete: (layerId: string) => void;
}

function LayerItem({
  layer,
  index,
  depth = 0,
  isSelected,
  isHovered,
  onSelect,
  onToggleVisibility,
  onToggleLock,
  onHover,
  onDelete,
}: LayerItemProps) {
  const icon = LAYER_ICONS[layer.type] || '?';
  const displayName =
    layer.type === 'primitive'
      ? PRIMITIVE_NAMES[layer.content.primitiveType] || layer.name
      : layer.name;

  return (
    <Draggable draggableId={layer.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`
            group flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer select-none
            ${isSelected ? 'bg-primary/20 text-white' : 'text-gray-300 hover:bg-white/5'}
            ${isHovered && !isSelected ? 'bg-white/10' : ''}
            ${snapshot.isDragging ? 'bg-primary/30 shadow-lg' : ''}
            ${!layer.visible ? 'opacity-50' : ''}
          `}
          style={{
            ...provided.draggableProps.style,
            paddingLeft: 8 + depth * 16,
          }}
          onClick={(e) => onSelect(layer.id, e)}
          onMouseEnter={() => onHover(layer.id)}
          onMouseLeave={() => onHover(null)}
        >
          {/* Expand/collapse for groups */}
          {layer.type === 'group' && (
            <span className="w-4 text-center text-gray-500">▼</span>
          )}

          {/* Icon */}
          <span className="w-5 text-center text-xs">{icon}</span>

          {/* Name */}
          <span className="flex-1 truncate">{displayName}</span>

          {/* Delete button (on hover) */}
          <button
            className="p-1 opacity-0 group-hover:opacity-100 text-gray-500 hover:text-red-400 hover:bg-white/10 rounded transition-opacity"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(layer.id);
            }}
            title="Delete layer"
          >
            ×
          </button>

          {/* Visibility toggle */}
          <button
            className={`p-1 hover:bg-white/10 rounded ${
              layer.visible ? 'text-gray-400' : 'text-gray-600'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleVisibility(layer.id);
            }}
            title={layer.visible ? 'Hide layer' : 'Show layer'}
          >
            {layer.visible ? '👁' : '👁‍🗨'}
          </button>

          {/* Lock toggle */}
          <button
            className={`p-1 hover:bg-white/10 rounded ${
              layer.locked ? 'text-yellow-500' : 'text-gray-600'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleLock(layer.id);
            }}
            title={layer.locked ? 'Unlock layer' : 'Lock layer'}
          >
            {layer.locked ? '🔒' : '🔓'}
          </button>
        </div>
      )}
    </Draggable>
  );
}

interface LayerListProps {
  layers: Layer[];
  parentId?: string;
  depth?: number;
  selectedLayerIds: string[];
  hoveredLayerId: string | null;
  onSelect: (layerId: string, event: React.MouseEvent) => void;
  onToggleVisibility: (layerId: string) => void;
  onToggleLock: (layerId: string) => void;
  onHover: (layerId: string | null) => void;
  onDelete: (layerId: string) => void;
}

function LayerList({
  layers,
  parentId,
  depth = 0,
  selectedLayerIds,
  hoveredLayerId,
  onSelect,
  onToggleVisibility,
  onToggleLock,
  onHover,
  onDelete,
}: LayerListProps) {
  return (
    <Droppable droppableId={parentId || 'root'} type="LAYER">
      {(provided) => (
        <div ref={provided.innerRef} {...provided.droppableProps}>
          {layers.map((layer, index) => (
            <React.Fragment key={layer.id}>
              <LayerItem
                layer={layer}
                index={index}
                depth={depth}
                isSelected={selectedLayerIds.includes(layer.id)}
                isHovered={hoveredLayerId === layer.id}
                onSelect={onSelect}
                onToggleVisibility={onToggleVisibility}
                onToggleLock={onToggleLock}
                onHover={onHover}
                onDelete={onDelete}
              />
              {/* Render children for groups */}
              {layer.type === 'group' && layer.children.length > 0 && (
                <LayerList
                  layers={layer.children}
                  parentId={layer.id}
                  depth={depth + 1}
                  selectedLayerIds={selectedLayerIds}
                  hoveredLayerId={hoveredLayerId}
                  onSelect={onSelect}
                  onToggleVisibility={onToggleVisibility}
                  onToggleLock={onToggleLock}
                  onHover={onHover}
                  onDelete={onDelete}
                />
              )}
            </React.Fragment>
          ))}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );
}

export function LayerPanel() {
  const [showAddMenu, setShowAddMenu] = useState(false);

  const {
    currentTemplate,
    selectedLayerIds,
    hoveredLayerId,
    selectLayer,
    selectLayers,
    setHoveredLayer,
    toggleLayerVisibility,
    toggleLayerLock,
    reorderLayers,
    addLayer,
    deleteLayer,
  } = useTemplateStore();

  const handleSelect = useCallback(
    (layerId: string, event: React.MouseEvent) => {
      if (event.shiftKey || event.metaKey || event.ctrlKey) {
        // Multi-select
        if (selectedLayerIds.includes(layerId)) {
          selectLayers(selectedLayerIds.filter((id) => id !== layerId));
        } else {
          selectLayers([...selectedLayerIds, layerId]);
        }
      } else {
        selectLayer(layerId);
      }
    },
    [selectedLayerIds, selectLayer, selectLayers]
  );

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return;

      const sourceIndex = result.source.index;
      const destIndex = result.destination.index;
      const sourceParentId = result.source.droppableId === 'root' ? undefined : result.source.droppableId;
      const destParentId = result.destination.droppableId === 'root' ? undefined : result.destination.droppableId;

      // For now, only support reordering within the same parent
      if (sourceParentId === destParentId) {
        reorderLayers(sourceIndex, destIndex, sourceParentId);
      }
    },
    [reorderLayers]
  );

  const handleAddLayer = useCallback(
    (layer: Layer) => {
      addLayer(layer);
      selectLayer(layer.id);
    },
    [addLayer, selectLayer]
  );

  if (!currentTemplate) {
    return (
      <div className="p-4 text-gray-500 text-sm text-center">
        No template loaded
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-white/10 relative">
        <h3 className="text-sm font-medium text-white">Layers</h3>
        <button
          className="p-1 text-gray-400 hover:text-white hover:bg-white/10 rounded text-lg leading-none"
          title="Add layer"
          onClick={() => setShowAddMenu(!showAddMenu)}
        >
          +
        </button>
        {showAddMenu && (
          <AddLayerMenu
            onAdd={handleAddLayer}
            onClose={() => setShowAddMenu(false)}
          />
        )}
      </div>

      {/* Layer list */}
      <div className="flex-1 overflow-y-auto">
        <DragDropContext onDragEnd={handleDragEnd}>
          <LayerList
            layers={currentTemplate.layers}
            selectedLayerIds={selectedLayerIds}
            hoveredLayerId={hoveredLayerId}
            onSelect={handleSelect}
            onToggleVisibility={toggleLayerVisibility}
            onToggleLock={toggleLayerLock}
            onHover={setHoveredLayer}
            onDelete={deleteLayer}
          />
        </DragDropContext>
      </div>

      {/* Selection info */}
      {selectedLayerIds.length > 0 && (
        <div className="px-3 py-2 border-t border-white/10 text-xs text-gray-500">
          {selectedLayerIds.length === 1
            ? '1 layer selected'
            : `${selectedLayerIds.length} layers selected`}
        </div>
      )}
    </div>
  );
}
